import { z } from "zod";
export const ProcessingStatusSchema = z.enum([
    "PENDING",
    "CREATED",
    "MANUAL_REVIEW",
    "IGNORED",
    "ERROR",
]);
export const PROCESSING_STATUSES = ProcessingStatusSchema.options;
export const OutlookCategoryColorSchema = z.enum([
    "preset0",
    "preset1",
    "preset2",
    "preset3",
    "preset4",
    "preset5",
    "preset6",
    "preset7",
    "preset8",
    "preset9",
    "preset10",
    "preset11",
    "preset12",
    "preset13",
    "preset14",
    "preset15",
    "preset16",
    "preset17",
    "preset18",
    "preset19",
    "preset20",
    "preset21",
    "preset22",
    "preset23",
    "preset24",
]);
export const OUTLOOK_CATEGORY_COLORS = OutlookCategoryColorSchema.options;
export const EVENT_SORT_FIELDS = [
    "emailReceivedAt",
    "startDate",
    "createdAt",
    "title",
];
export const EventSortFieldSchema = z.enum(EVENT_SORT_FIELDS);
export const SortOrderSchema = z.enum(["asc", "desc"]);
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 100;
// Query params arrive as strings, so we coerce numbers and split CSVs here.
export const EventListQuerySchema = z.object({
    status: z
        .string()
        .optional()
        .transform((value, ctx) => {
        if (value === undefined || value === "")
            return undefined;
        const parts = value
            .split(",")
            .map((p) => p.trim())
            .filter((p) => p.length > 0);
        const parsed = [];
        for (const part of parts) {
            const result = ProcessingStatusSchema.safeParse(part);
            if (!result.success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Valore status non valido: '${part}'`,
                });
                return z.NEVER;
            }
            parsed.push(result.data);
        }
        return parsed.length > 0 ? parsed : undefined;
    }),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    startDateFrom: z.string().optional(),
    startDateTo: z.string().optional(),
    search: z
        .string()
        .optional()
        .transform((value) => {
        if (value === undefined)
            return undefined;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }),
    page: z.coerce.number().int().min(1).optional().default(1),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(MAX_PAGE_SIZE)
        .optional()
        .default(DEFAULT_PAGE_SIZE),
    sortBy: EventSortFieldSchema.optional().default("emailReceivedAt"),
    sortOrder: SortOrderSchema.optional().default("desc"),
    priority: z
        .string()
        .optional()
        .transform((value, ctx) => {
        if (value === undefined || value === "")
            return undefined;
        const parts = value
            .split(",")
            .map((p) => p.trim())
            .filter((p) => p.length > 0);
        const result = [];
        for (const part of parts) {
            const n = Number(part);
            if (!Number.isInteger(n) || n < 0 || n > 3) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Valore priority non valido: '${part}' (deve essere 0-3)`,
                });
                return z.NEVER;
            }
            result.push(n);
        }
        return result.length > 0 ? result : undefined;
    }),
    priorityMin: z.coerce.number().int().min(0).max(3).optional(),
    categoryId: z
        .string()
        .optional()
        .transform((value) => {
        if (value === undefined || value === "")
            return undefined;
        const ids = value
            .split(",")
            .map((p) => p.trim())
            .filter((p) => p.length > 0);
        return ids.length > 0 ? ids : undefined;
    }),
    unseen: z
        .preprocess((v) => (v === "1" || v === "true" ? true : undefined), z.literal(true).optional()),
    isImportant: z
        .preprocess((v) => (v === "1" || v === "true" ? true : undefined), z.literal(true).optional()),
    hideMultiDay: z
        .preprocess((v) => (v === "1" || v === "true" ? true : undefined), z.literal(true).optional()),
    uncategorized: z
        .preprocess((v) => (v === "1" || v === "true" ? true : undefined), z.literal(true).optional()),
});
// Dates are serialized as ISO strings over the wire; the FE parses them back
// with new Date() when needed.
const IsoDateString = z.string().datetime({ offset: true });
export const EventCategorySchema = z.object({
    id: z.string(),
    name: z.string().min(1),
    description: z.string().nullable(),
    outlookColor: OutlookCategoryColorSchema,
    isActive: z.boolean(),
    createdAt: IsoDateString,
    updatedAt: IsoDateString,
});
export const EventCategoryListResponseSchema = z.object({
    items: z.array(EventCategorySchema),
});
export const EventCategoryCreateSchema = z
    .object({
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    outlookColor: OutlookCategoryColorSchema.optional(),
})
    .strict();
export const EventCategoryUpdateSchema = z
    .object({
    name: z.string().min(1).max(100).optional(),
    description: z.string().max(500).nullable().optional(),
    outlookColor: OutlookCategoryColorSchema.optional(),
    isActive: z.boolean().optional(),
})
    .strict()
    .refine((o) => Object.keys(o).length > 0, {
    message: "Almeno un campo richiesto",
});
export const EventListItemSchema = z.object({
    id: z.string(),
    emailSubject: z.string(),
    emailFrom: z.string(),
    emailReceivedAt: IsoDateString,
    title: z.string().nullable(),
    description: z.string().nullable(),
    startDate: IsoDateString.nullable(),
    endDate: IsoDateString.nullable(),
    location: z.string().nullable(),
    organizer: z.string().nullable(),
    eventType: z.string().nullable(),
    categoryId: z.string().nullable().optional(),
    category: EventCategorySchema.nullable().optional(),
    status: ProcessingStatusSchema,
    confidence: z.number().nullable(),
    priority: z.number().int().min(0).max(3),
    isImportant: z.boolean(),
    isMultiDay: z.boolean(),
    seenAt: IsoDateString.nullable(),
    createdAt: IsoDateString,
    updatedAt: IsoDateString,
});
const EventActorSchema = z
    .object({ id: z.string(), name: z.string(), email: z.string() })
    .nullable();
export const EventDetailSchema = EventListItemSchema.extend({
    emailMessageId: z.string(),
    emailBody: z.string().nullable(),
    calendarEventId: z.string().nullable(),
    errorMessage: z.string().nullable(),
    retryCount: z.number().int(),
    stage1Verdict: z.string().nullable(),
    // Best-effort JSON parse of the raw OpenAI response. If parsing fails
    // (legacy rows, truncated payloads) we keep the raw string so the UI can
    // still show something.
    rawAiResponse: z
        .union([z.record(z.string(), z.unknown()), z.string()])
        .nullable(),
    prioritySetBy: EventActorSchema,
    prioritySetAt: IsoDateString.nullable(),
    reviewedBy: EventActorSchema,
    reviewedAt: IsoDateString.nullable(),
});
export const EventListResponseSchema = z.object({
    items: z.array(EventListItemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    totalPages: z.number().int().nonnegative(),
});
export const SeenResponseSchema = z.object({
    id: z.string(),
    seenAt: IsoDateString.nullable(),
});
// Action payloads (phase 3C). The update schema accepts a partial patch on the
// editable fields; all keys are optional but at least one must be present.
// strict() rejects unknown keys so a typo client-side gets a 400 instead of
// silently being ignored.
const NullableIsoDate = z
    .string()
    .datetime({ offset: true })
    .nullable()
    .optional();
export const EventUpdateSchema = z
    .object({
    title: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    startDate: NullableIsoDate,
    endDate: NullableIsoDate,
    location: z.string().nullable().optional(),
    organizer: z.string().nullable().optional(),
    eventType: z.string().nullable().optional(),
    categoryId: z.string().nullable().optional(),
    // User-controlled status transitions only; PENDING and ERROR are system states.
    status: z.enum(["MANUAL_REVIEW", "CREATED", "IGNORED"]).optional(),
})
    .strict()
    .refine((o) => Object.keys(o).length > 0, {
    message: "Almeno un campo richiesto",
});
export const EventRejectSchema = z
    .object({
    reason: z.string().max(500).optional(),
})
    .strict();
export const EventPrioritySchema = z
    .object({
    priority: z.number().int().min(0).max(3),
})
    .strict();
export const EventBulkPrioritySchema = z
    .object({
    ids: z.array(z.string()).min(1),
    priority: z.number().int().min(0).max(3),
})
    .strict();
const StatusCountSchema = z.object({
    PENDING: z.number().int().nonnegative(),
    CREATED: z.number().int().nonnegative(),
    MANUAL_REVIEW: z.number().int().nonnegative(),
    IGNORED: z.number().int().nonnegative(),
    ERROR: z.number().int().nonnegative(),
});
export const HealthRuntimeSchema = z.object({
    status: z.enum(["ok", "degraded"]),
    uptime: z.number().nonnegative(),
    lastCycleAt: IsoDateString.nullable(),
    lastCycleSuccess: z.boolean(),
});
const PriorityDistributionSchema = z.object({
    unrated: z.number().int().nonnegative(),
    p1: z.number().int().nonnegative(),
    p2: z.number().int().nonnegative(),
    p3: z.number().int().nonnegative(),
});
export const PipelineMetricsSchema = z.object({
    windowMinutes: z.number().int().positive(),
    cycleDurationMs: z.object({
        last: z.number().nullable(),
        avg: z.number().nullable(),
        samples: z.number().int().nonnegative(),
    }),
    stage1: z.object({
        avgMs: z.number().nullable(),
        p95Ms: z.number().nullable(),
        callCount: z.number().int().nonnegative(),
    }),
    stage2: z.object({
        avgMs: z.number().nullable(),
        p95Ms: z.number().nullable(),
        hitRatePct: z.number().nullable(),
    }),
});
export const StatsResponseSchema = z.object({
    totals: StatusCountSchema,
    manualReviewOpen: z.number().int().nonnegative(),
    errorOpen: z.number().int().nonnegative(),
    unseenCount: z.number().int().nonnegative().optional().default(0),
    last24h: z.object({
        processed: z.number().int().nonnegative(),
        totals: StatusCountSchema,
    }),
    health: HealthRuntimeSchema,
    priorityStats: z.object({
        unrated: z.number().int().nonnegative(),
        distribution: PriorityDistributionSchema,
    }),
    pipeline: PipelineMetricsSchema,
});
export const MetricsQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(90).optional().default(30),
});
export const MetricsPointSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    total: z.number().int().nonnegative(),
    totals: StatusCountSchema,
});
export const MetricsResponseSchema = z.object({
    days: z.number().int().positive(),
    items: z.array(MetricsPointSchema),
});
//# sourceMappingURL=events.js.map